import React, { useEffect, useMemo, useState } from "react";
import {
  FaCloudUploadAlt,
  FaCog,
  FaDownload,
  FaExchangeAlt,
  FaFileAlt,
  FaGlobeAmericas,
  FaPlus,
  FaSearch,
  FaStore,
  FaUserTie,
  FaWallet,
} from "react-icons/fa";
import "../styles/ReportingPage.css";
import {
  getReportSummary,
  getReportVolumeTrends,
  getReportRecentActivity,
  generateMerchantReport,
  generateAgentReport,
  generateTransactionReport,
} from "../api/api";
import { getOrgId, getUserData } from "../utils/authStorage";
import { useToast } from "../context/ToastContext";

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const pick = (res) => res?.data ?? res ?? null;
const asArray = (raw) =>
  (Array.isArray(raw) && raw) ||
  (Array.isArray(raw?.data) && raw.data) ||
  (Array.isArray(raw?.categories) && raw.categories) ||
  (Array.isArray(raw?.items) && raw.items) ||
  (Array.isArray(raw?.reports) && raw.reports) ||
  [];

/* Fixed category shells; live stats are merged in from the API by type keyword. */
const DEFAULT_CATEGORIES = [
  { key: "merchant", title: "Merchant Reports", subtitle: "Settlement reconciliations & fee audits", icon: <FaStore /> },
  { key: "agent", title: "Agent Reports", subtitle: "Commission tracking & performance", icon: <FaUserTie /> },
  { key: "transaction", title: "Transactions", subtitle: "Full transaction history & raw data", icon: <FaExchangeAlt /> },
  { key: "settlement", title: "Settlements", subtitle: "Payout cycles & bank transfers", icon: <FaWallet /> },
];

const generatorFor = (key) => {
  if (key.includes("agent")) return generateAgentReport;
  if (key.includes("transaction")) return generateTransactionReport;
  if (key.includes("settle")) return generateTransactionReport;
  return generateMerchantReport;
};

const Analytics = ({ isSidebarOpen }) => {
  const { showSuccess, showError } = useToast();
  const userData = getUserData();
  const user = userData?.user || userData?.data || userData || {};
  const orgId = getOrgId() || user?.profile?.orgId || user?.orgId || user?.organizationId || null;

  const [search, setSearch] = useState("");
  const [summary, setSummary] = useState(null);
  const [apiCategories, setApiCategories] = useState([]);
  const [volumeBars, setVolumeBars] = useState([0, 0, 0, 0, 0, 0, 0]);
  const [volumeMeta, setVolumeMeta] = useState({ label: "Last 7 Days", change: "0%" });
  const [recentRows, setRecentRows] = useState([]);
  const [busyKey, setBusyKey] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    const run = async () => {
      const results = await Promise.allSettled([
        getReportSummary(orgId),
        getReportVolumeTrends(orgId),
        getReportRecentActivity(orgId, 1, 10),
      ]);
      if (cancelled) return;
      const val = (i) => (results[i].status === "fulfilled" ? pick(results[i].value) : null);

      const sum = val(0) || {};
      setSummary(sum);
      setApiCategories(asArray(sum.categories ?? sum));

      // Volume trends → bar heights (normalized 0..100)
      const trendRaw = val(1);
      const trendArr = asArray(trendRaw).map((d) =>
        typeof d === "number" ? d : num(d?.value ?? d?.amount ?? d?.volume ?? d?.total)
      );
      const maxT = Math.max(...trendArr, 0);
      setVolumeBars(trendArr.length ? trendArr.map((v) => (maxT ? Math.round((v / maxT) * 100) : 0)) : [0, 0, 0, 0, 0, 0, 0]);
      setVolumeMeta({
        label: trendRaw?.label ?? trendRaw?.range ?? "Last 7 Days",
        change: trendRaw?.change ?? trendRaw?.growth ?? "0%",
      });

      // Recent activity rows
      setRecentRows(
        asArray(val(2)).map((r, idx) => ({
          id: r.id ?? r._id ?? `${r.name ?? "report"}-${idx}`,
          name: r.name ?? r.reportName ?? r.fileName ?? "Report",
          type: r.type ?? r.category ?? "—",
          status: String(r.status ?? "Ready"),
          date: r.date ?? r.createdAt ?? r.generatedAt ?? "—",
        }))
      );
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [orgId, refreshKey]);

  /* Merge live stats onto the fixed category shells */
  const categories = useMemo(() => {
    return DEFAULT_CATEGORIES.map((cat) => {
      const match = apiCategories.find((c) => {
        const t = String(c?.type ?? c?.key ?? c?.name ?? c?.title ?? "").toLowerCase();
        return t.includes(cat.key);
      });
      return {
        ...cat,
        lastRun: match?.lastRun ?? match?.lastGenerated ?? "—",
        avgSize: match?.avgSize ?? match?.averageSize ?? "0 MB",
      };
    });
  }, [apiCategories]);

  const metrics = [
    { title: "Total Exports", value: String(num(summary?.totalExports ?? summary?.exports ?? summary?.totalReports)), icon: <FaFileAlt />, meta: "Files generated" },
    { title: "Scheduled", value: String(num(summary?.scheduled ?? summary?.scheduledJobs ?? summary?.automations)), icon: <FaCog />, meta: "Automations active" },
    {
      title: "Storage Used",
      value: `${num(summary?.storageUsed)}GB/${num(summary?.storageLimit ?? 5)}GB`,
      icon: <FaCloudUploadAlt />,
      meta: `${num(summary?.utilization ?? summary?.storagePercent)}% utilization`,
      progress: num(summary?.utilization ?? summary?.storagePercent),
    },
  ];

  const filteredRows = useMemo(
    () =>
      recentRows.filter((row) =>
        `${row.name} ${row.type} ${row.status}`.toLowerCase().includes(search.toLowerCase())
      ),
    [recentRows, search]
  );

  const handleGenerate = async (cat) => {
    if (!orgId) {
      showError("Error", "No organization selected", 3000);
      return;
    }
    if (busyKey) return;
    try {
      setBusyKey(cat.key);
      const res = await generatorFor(cat.key)(orgId);
      const url = res?.data?.url ?? res?.url ?? res?.data?.downloadUrl ?? res?.downloadUrl;
      showSuccess("Report", res?.message || `${cat.title} generated`);
      if (url) window.open(url, "_blank", "noopener");
      setRefreshKey((k) => k + 1);
    } catch (err) {
      showError("Error", err?.response?.data?.message || "Failed to generate report", 3000);
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className={`rp-page${isSidebarOpen ? " shifted" : ""}`}>
      <div className="rp-topbar">
        <div className="rp-search">
          <FaSearch />
          <input
            type="text"
            placeholder="Search reports..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="rp-metrics">
        {metrics.map((item) => (
          <article className="rp-card rp-metric" key={item.title}>
            <div className="rp-metric__icon">{item.icon}</div>
            <div>
              <small>{item.title}</small>
              <strong>{item.value}</strong>
              {item.progress ? (
                <div className="rp-progress">
                  <span style={{ width: `${item.progress}%` }} />
                </div>
              ) : null}
            </div>
          </article>
        ))}
      </div>

      <div className="rp-grid">
        <section className="rp-main">
          <div className="rp-section-head">
            <h3>Report Categories</h3>
          </div>

          <div className="rp-categories">
            {categories.map((card) => (
              <article key={card.title} className="rp-card rp-category">
                <div className="rp-category__head">
                  <div>
                    <h4>{card.title}</h4>
                    <p>{card.subtitle}</p>
                  </div>
                  <span>{card.icon}</span>
                </div>
                <div className="rp-category__stats">
                  <div>
                    <small>Last Run</small>
                    <strong>{card.lastRun}</strong>
                  </div>
                  <div>
                    <small>Avg Size</small>
                    <strong>{card.avgSize}</strong>
                  </div>
                </div>
                <button
                  type="button"
                  className="rp-generate"
                  disabled={busyKey === card.key}
                  onClick={() => handleGenerate(card)}
                >
                  {busyKey === card.key ? "Generating…" : "Generate Report"}
                </button>
              </article>
            ))}
          </div>

          <article className="rp-card rp-activity">
            <div className="rp-activity__head">
              <h3>Recent Activity</h3>
              <button type="button">View all</button>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Report Name</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center", padding: 24, opacity: 0.6 }}>
                      No reports found.
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.name}</td>
                      <td>{row.type}</td>
                      <td>
                        <span className={`rp-badge rp-badge--${row.status.toLowerCase()}`}>{row.status}</span>
                      </td>
                      <td>{row.date}</td>
                      <td>
                        <button type="button" className="rp-action-btn" title="Download">
                          <FaDownload />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </article>
        </section>

        <aside className="rp-side">
          <button type="button" className="rp-new-btn">
            <FaPlus /> New Report
          </button>

          <article className="rp-card rp-side-card">
            <h4>Volume Trends</h4>
            <div className="rp-trend-bars">
              {volumeBars.map((bar, idx) => (
                <span key={idx} style={{ height: `${bar}%` }} />
              ))}
            </div>
            <div className="rp-trend-meta">
              <small>{volumeMeta.label}</small>
              <strong>{volumeMeta.change}</strong>
            </div>
          </article>

          <article className="rp-card rp-map">
            <FaGlobeAmericas />
            <h5>Live Global Activity</h5>
            <p>Real-time sync active</p>
          </article>
        </aside>
      </div>
    </div>
  );
};

export default Analytics;
